import type {SiteLocale} from "seo/siteMeta";
import type {ContentPageEntry} from "./contentPages.ts";
import {CORPUS_SECTIONS, corpusSectionOf, type CorpusSection} from "./corpusIndex.ts";

/**
 * Which corpus pages the Resources → Guides surface offers, per locale (ui#1555).
 *
 * The Resources disclosure carried a GUIDES COLUMN of four hand-listed content keys (ui#1540). The
 * owner asked for one row under Explore opening a dialog instead, so the set of pages stopped being a
 * hand-kept list and is derived from the corpus's OWN sections — the same ones {@link corpusSectionOf}
 * groups the `/trimite-fisiere-mari` index by. A page therefore joins the picker on the day its
 * markdown file lands, which is the whole point of the content pipeline.
 *
 * React-free (the `logic-outside-react` rule): the nav asks it which row to render, the dialog asks it
 * what to list, and the unit tests call it directly.
 */

/**
 * The corpus fields this module reads.
 *
 * Narrower than {@link ContentPageEntry} on purpose: every function below is generic over it, so the
 * nav can pass whole `virtual:content-index` entries (body thunk included) and a test can pass three
 * literal fields without inventing a `load` that nothing calls.
 */
export type GuideCorpusPage = Pick<ContentPageEntry, "key" | "locale" | "path">;

/**
 * The content key of the head-term guide both languages ship — `src/content/en/send-large-files.md`
 * and `src/content/ro/trimite-fisiere-mari.md`.
 *
 * A KEY rather than a path, for the same reason the retired guides column used keys: the slug is
 * localized and the key is not. It names the overview the picker opens on and the page the nav row
 * links at, so a middle-click on "Guides" lands on the same article a plain click shows in the dialog.
 */
export const GUIDE_OVERVIEW_CONTENT_KEY = "send-large-files";

/**
 * The corpus sections a guide picker lists.
 *
 * `guides` is the `/ghid` + `/guide` tree. `more` is the catch-all for pages that sit at the tree root
 * — the head-term overview and the WeTransfer alternative — which read as general how-to articles
 * rather than as one audience's or one competitor's landing page. `verticals` (`/pentru`) and
 * `comparisons` (`/comparatie`) are deliberately out: they are the corpus's audience and competitor
 * pages, and a menu row labelled "Guides" that opened onto them would be naming them wrongly. They
 * stay reachable through the corpus index block on the overview page, which lists all four sections.
 */
const GUIDE_SECTIONS: ReadonlySet<CorpusSection> = new Set<CorpusSection>(["guides", "more"]);

/** The Guides row as the nav needs it: the crawlable href, and everything the dialog will list. */
export interface GuideNavEntry<T extends GuideCorpusPage> {
	/** This locale's path for the overview guide — a real page, not a dialog-only address. */
	to: string;
	/**
	 * The pages the dialog offers, overview first.
	 *
	 * Typed non-empty because it always contains {@link to}'s own page — which is what lets the dialog
	 * treat "the first choice" as a real page instead of carrying a branch for a state this function
	 * returns `null` for.
	 */
	choices: readonly [T, ...T[]];
}

/**
 * The guides one locale ships: the overview, then the corpus's own reading order.
 *
 * The overview leads because it is what the picker opens on and what the nav row points at. The rest
 * are grouped by {@link CORPUS_SECTIONS} and, inside a section, keep the order the corpus reader
 * produced — a filename sort. That is deliberately the SAME order `corpusIndexSections` renders the
 * index block on the overview page in: one reading order for the corpus, not two that drift. Without
 * the section pass the raw filename sort alone would put `/alternativa-wetransfer` ahead of the whole
 * `/ghid` tree, because that is where its filename sorts.
 *
 * A locale that ships none returns an empty list rather than borrowing the other tree's pages: the
 * cross-locale mirror routes exist so in-app links resolve, not so a menu can advertise a Romanian
 * article from the English chrome at a URL no sitemap lists.
 */
export function guideChoicesFor<T extends GuideCorpusPage>(
	entries: readonly T[],
	locale: SiteLocale,
): T[] {
	const shipped = entries.filter((entry) =>
		entry.locale === locale && GUIDE_SECTIONS.has(corpusSectionOf(entry.path)));
	// A STABLE sort, so pages inside one section keep the filename order they arrived in.
	shipped.sort((a, b) =>
		CORPUS_SECTIONS.indexOf(corpusSectionOf(a.path)) - CORPUS_SECTIONS.indexOf(corpusSectionOf(b.path)));
	const overview = shipped.find((entry) => entry.key === GUIDE_OVERVIEW_CONTENT_KEY);
	return overview === undefined
		? shipped
		: [overview, ...shipped.filter((entry) => entry !== overview)];
}

/**
 * The Guides row for one locale, or `null` when it must not render at all.
 *
 * `null` when the locale ships no overview page, because the row is an ANCHOR before it is a dialog
 * opener: it needs a real page to point at, and a row that only opened a dialog would be the one entry
 * in this menu with no URL to middle-click, no crawlable target, and nothing to fall back to before
 * the bar hydrates.
 */
export function guideNavEntryFor<T extends GuideCorpusPage>(
	entries: readonly T[],
	locale: SiteLocale,
): GuideNavEntry<T> | null {
	const [overview, ...rest] = guideChoicesFor(entries, locale);
	return overview === undefined || overview.key !== GUIDE_OVERVIEW_CONTENT_KEY
		? null
		: {to: overview.path, choices: [overview, ...rest]};
}
